import { LoadingOutlined } from '@ant-design/icons'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { getVirtualIpDraft, type VirtualIpDraft } from '../../api'
import type { VirtualIpContextRef } from './videoDirectorTypes'

/**
 * Spec §三 — real fields only. `getVirtualIpDraft` (the *only* Virtual
 * IP surface this workspace currently has) is a single Positioning
 * Draft per workspace, not a multi-IP catalog — there is no `name`,
 * `avatar`, or `version` column on it. So this card never renders a
 * "选择虚拟 IP" list or invented IP identity fields; instead it honestly
 * reflects this workspace's one real draft: its completion status and
 * which real `virtual_ip_intake_v1` sections actually have data,
 * so "本次 AI 创作将参考" only checks items that truly exist.
 */

const INTAKE_KEYS: Array<keyof NonNullable<VirtualIpDraft['intake']>['intake']> = [
  'business_goals',
  'commercial_direction',
  'target_audience',
  'content_preferences',
  'target_platforms',
  'desired_first_impressions',
]

function hasRealValue(v: unknown): boolean {
  if (v === null || v === undefined) return false
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === 'string') return v.trim().length > 0
  if (typeof v === 'object') return Object.values(v as Record<string, unknown>).some(hasRealValue)
  return true
}

export default function VirtualIpContextCard({
  onResolved,
}: {
  onResolved: (ref: VirtualIpContextRef | null) => void
}) {
  const { t } = useTranslation('video')
  const { workspaceId = '' } = useParams()
  const navigate = useNavigate()
  const [draft, setDraft] = useState<VirtualIpDraft | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const intakeChecklist = useMemo(
    () =>
      INTAKE_KEYS.map((key) => ({
        key,
        label: t(`director.virtualIp.intake.${key}`),
      })),
    [t],
  )

  useEffect(() => {
    if (!workspaceId) return
    let alive = true
    setLoading(true)
    getVirtualIpDraft(workspaceId)
      .then(({ data }) => {
        if (!alive) return
        setDraft(data)
        onResolved({
          draft_id: data.id,
          virtual_ip_version: data.questionnaire_version || null,
          has_intake: !!data.intake,
        })
      })
      .catch((e) => {
        if (!alive) return
        setError(e?.response?.data?.detail?.message || t('director.virtualIp.loadFailed'))
        onResolved(null)
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, t])

  if (loading) {
    return (
      <div className="tc-vdc-vip-card">
        <LoadingOutlined /> {t('director.virtualIp.loading')}
      </div>
    )
  }

  if (error || !draft) {
    return <div className="tc-vdc-vip-card tc-vdc-vip-card-error">{error || t('director.virtualIp.notLoaded')}</div>
  }

  const intake = draft.intake?.intake

  return (
    <div className="tc-vdc-vip-card">
      <div className="tc-vdc-vip-card-head">
        <div className="tc-vdc-vip-card-title">{t('director.virtualIp.cardTitle')}</div>
        <span className={`tc-badge-${draft.status === 'step_1_completed' ? 'success' : 'warning'}`}>
          {draft.status === 'step_1_completed' ? t('director.virtualIp.statusCompleted') : t('director.virtualIp.statusDraft')}
        </span>
      </div>

      <div className="tc-vdc-vip-card-sub">{t('director.virtualIp.referenceIntro')}</div>
      <ul className="tc-vdc-vip-checklist">
        {intakeChecklist.map((item) => {
          const filled = !!intake && hasRealValue(intake[item.key])
          return (
            <li key={item.key} className={filled ? 'is-filled' : 'is-empty'}>
              <span className="tc-vdc-vip-checklist-mark">{filled ? '✓' : '—'}</span> {item.label}
            </li>
          )
        })}
      </ul>

      {!draft.intake && <div className="tc-vdc-vip-card-hint">{t('director.virtualIp.noIntake')}</div>}

      <button
        type="button"
        className="tc-vdc-vip-card-link"
        onClick={() => navigate(`/workspace/${workspaceId}/virtual-ip-positioning`)}
      >
        {t('director.virtualIp.viewProfile')}
      </button>
    </div>
  )
}
